import { 
  AIApplication, 
  AIOverviewData, 
  ProcessedAIData,
  CrossoverTeamMemberActivity,
  CrossoverGroup
} from '../types';
import { calculateTimeStats } from './timeCalculationService';

/**
 * Extract AI applications from a single team member's activity data
 */
export const extractAIApplicationsFromActivity = (activity: CrossoverTeamMemberActivity): AIApplication[] => {
  const aiApplications: AIApplication[] = [];
  
  if (!activity.grouping || !activity.grouping.advancedGroups) {
    return aiApplications;
  }

  // Filter groups for AI and PURE_AI sections
  const aiGroups = activity.grouping.advancedGroups.filter((group: CrossoverGroup) => 
    group.sectionName === 'AI' || group.sectionName === 'PURE_AI'
  );

  // Extract applications from each AI group
  aiGroups.forEach((group: CrossoverGroup) => {
    group.groupItems.forEach((app) => {
      aiApplications.push({
        applicationName: app.applicationName,
        applicationId: app.applicationId,
        spentTime: app.spentTime,
        sectionName: group.sectionName as 'AI' | 'PURE_AI',
        categoryColor: app.categoryColor,
        activityLevel: app.activityLevel
      });
    });
  });

  return aiApplications;
};

/**
 * Process AI data for a single team member
 */
export const processTeamMemberAIData = (activity: CrossoverTeamMemberActivity): AIOverviewData | null => {
  if (!activity.assignment || !activity.assignment.candidate) {
    console.warn('Missing assignment or candidate data for team member');
    return null;
  }

  // Use standardized time calculation service for consistency
  const timeStats = calculateTimeStats({
    grouping: activity.grouping || { advancedGroups: [] }
  });
  
  // Extract AI applications for detailed breakdown
  const aiApplications = extractAIApplicationsFromActivity(activity);
  
  // Use standardized time calculations (this ensures consistency with Team AI Leaderboard)
  const totalAITime = timeStats.cursorTime; // Cursor time (from AI section)
  const totalPureAITime = timeStats.aiTime;  // Other AI tools (AI + PURE_AI sections excluding cursor)
  const totalCombinedAITime = totalAITime + totalPureAITime;

  // Only return data if there's AI usage
  if (aiApplications.length === 0 && totalCombinedAITime === 0) {
    return null;
  }

  return {
    teamMemberName: activity.assignment.candidate.printableName,
    email: activity.assignment.candidate.email,
    aiApplications,
    totalAITime,
    totalPureAITime,
    totalCombinedAITime
  };
};

/**
 * Process comprehensive AI data from all team activities
 */
export const processComprehensiveAIData = (activities: CrossoverTeamMemberActivity[]): ProcessedAIData => {
  console.log('🤖 [AI-OVERVIEW] Processing AI data for', activities.length, 'team members');
  
  // Process each team member's AI data
  const userAIData: AIOverviewData[] = [];
  
  activities.forEach((activity, index) => {
    const memberAIData = processTeamMemberAIData(activity);
    if (memberAIData) {
      userAIData.push(memberAIData);
      console.log(`🤖 [AI-OVERVIEW] Processed AI data for ${memberAIData.teamMemberName}: ${memberAIData.totalCombinedAITime}m total`);
    } else {
      console.log(`🤖 [AI-OVERVIEW] No AI data for team member ${index + 1}`);
    }
  });

  // Calculate overall statistics
  const overallAITime = userAIData.reduce((sum, user) => sum + user.totalAITime, 0);
  const overallPureAITime = userAIData.reduce((sum, user) => sum + user.totalPureAITime, 0);

  // Calculate most used AI applications across all users
  const appUsageMap = new Map<string, { totalTime: number; userCount: number }>();
  
  userAIData.forEach(user => {
    const userApps = new Set<string>();
    user.aiApplications.forEach(app => {
      const existing = appUsageMap.get(app.applicationName) || { totalTime: 0, userCount: 0 };
      existing.totalTime += app.spentTime;
      
      // Only increment user count once per user per app
      if (!userApps.has(app.applicationName)) {
        existing.userCount += 1;
        userApps.add(app.applicationName);
      }
      
      appUsageMap.set(app.applicationName, existing);
    });
  });

  const mostUsedAIApps = Array.from(appUsageMap.entries())
    .map(([applicationName, stats]) => ({
      applicationName,
      totalTime: stats.totalTime,
      userCount: stats.userCount
    }))
    .sort((a, b) => b.totalTime - a.totalTime)
    .slice(0, 10); // Top 10 most used apps

  const result = {
    users: userAIData.sort((a, b) => b.totalCombinedAITime - a.totalCombinedAITime), // Sort by total AI time descending
    totalUsers: userAIData.length,
    overallAITime,
    overallPureAITime,
    mostUsedAIApps
  };

  console.log('🤖 [AI-OVERVIEW] AI data processing completed:', {
    totalUsers: result.totalUsers,
    overallAITime,
    overallPureAITime,
    topApps: mostUsedAIApps.slice(0, 3).map(app => `${app.applicationName}: ${app.totalTime}m`)
  });

  return result;
};

/**
 * Format time in minutes to human-readable format
 */
export const formatAITime = (minutes: number): string => {
  if (minutes === 0) return '0m';
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours === 0) {
    return `${remainingMinutes}m`;
  } else if (remainingMinutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${remainingMinutes}m`;
  }
};

/**
 * Get AI applications grouped by section (AI vs PURE_AI)
 */
export const getAIApplicationsBySection = (aiData: AIOverviewData): { ai: AIApplication[], pureAI: AIApplication[] } => {
  const ai = aiData.aiApplications.filter(app => app.sectionName === 'AI');
  const pureAI = aiData.aiApplications.filter(app => app.sectionName === 'PURE_AI');
  
  return { ai, pureAI };
}; 